import * as React from 'react';
import { MetricEntry } from 'common/query-builder/query-entry-section/types';
import { MetricType } from 'common/types/reports/bookmark';
import { EntryRowProps } from 'iron/common/query-builder/query-entry-section/row-types/types';
import { SectionItem } from 'common/components/items-menu/types';
import { FormulaDefinition } from './formula/definition';
import { FormulaMeasurement } from './formula/measurement';
import { FunnelBehaviorDefinition } from './funnel/behavior-definition';
import { FunnelMetricMeasurement } from './funnel/measurement';
import { RetentionBehaviorDefinition } from './retention/behavior-definition';
import { RetentionMetricMeasurement } from './retention/measurement';
import { SimpleBehaviorDefinition } from './simple/behavior-definition';
import { SimpleMetricMeasurement } from './simple/measurement';
import { UserBehaviorDefinition } from './user/behavior-definition';
import { UserMetricMeasurement } from './user/measurement';
import { simpleMetricCustomActions } from './simple/utils';
import { ValidMetricTypes } from './constants';
import { userMetricCustomActions } from './user/utils';
export const ValidEntryRowProps = ["currentChartType", "dataGroups", "dispatchTrack", "getEntries", "getEntryTrackingProps", "index", "rowLabel", "sectionProps"] as const;

/** Defines inherited properties from QueryEntrySection rows */
export type InheritedEntryRowProps = Pick<EntryRowProps<MetricEntry>, (typeof ValidEntryRowProps)[number]>;
export type ActiveMenu = `customizeBuckets`;
export type RowProps = InheritedEntryRowProps & {
  entry: MetricEntry;
  disableHideEntry?: boolean;
  isExpanded?: boolean;
  updateEntry: (entry: MetricEntry, opts?: {
    index?: number;
    requery?: boolean;
    withUnsavedChanges?: boolean;
  }) => void;
  removeEntry: (() => void) | null;
  cloneEntry: ((entry: MetricEntry) => void) | null;
  /** Indicates a given entry is inside the referenced metrics section of a formula */
  parentMetricIndex?: number;
  contextPanelParentElement?: HTMLElement | null;
  /** used for staging certain blocks in a metric */
  setActiveMenu?: (menu: ActiveMenu | null) => void;
  activeMenu?: ActiveMenu | null;
};
type MetricPartsType = { [key in ValidMetricTypes]: {
  /** renders metric measurement blocks */
  measurement: (props: RowProps) => React.ReactNode;
  /** renders metric definitions like formula body, metric events, etc... */
  definition: (props: RowProps) => React.ReactNode;
  /** provides a way to display additional overflow action for metrics */
  customActions?: (props: RowProps) => SectionItem[];
} };

/**
 * For each metric, there's a behavior and measurement definition
 * - Measurement defines how a given behavior count is calculated, example: "uniques on Purchase", "totals on Funnel A -> B"
 * - Definition contains the actual event selections & other settings representing a funnel, retention or other types of behaviors
 */
export const MetricParts: MetricPartsType = {
  [MetricType.Simple]: {
    definition: SimpleBehaviorDefinition,
    measurement: SimpleMetricMeasurement,
    customActions: simpleMetricCustomActions
  },
  [MetricType.Funnel]: {
    definition: FunnelBehaviorDefinition,
    measurement: FunnelMetricMeasurement
  },
  [MetricType.Retention]: {
    definition: RetentionBehaviorDefinition,
    measurement: RetentionMetricMeasurement
  },
  [MetricType.RetentionFrequency]: {
    definition: RetentionBehaviorDefinition,
    measurement: RetentionMetricMeasurement
  },
  [MetricType.People]: {
    definition: UserBehaviorDefinition,
    measurement: UserMetricMeasurement,
    customActions: userMetricCustomActions
  },
  [MetricType.Cohort]: {
    definition: UserBehaviorDefinition,
    measurement: UserMetricMeasurement
  },
  [MetricType.Formula]: {
    definition: FormulaDefinition,
    measurement: FormulaMeasurement
  }
};